import { InformationSection } from "@clipboard-health/ui-components";
import { type UseModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { DialogContent, Stack } from "@mui/material";
import { useGetExtraTimePaySettings } from "@src/appV2/Facilities/api/useGetExtraTimePaySettings";
import { useGetWorkplace } from "@src/appV2/Facilities/api/useGetWorkplace";
import { FullScreenDialog } from "@src/appV2/lib/Dialogs";
import { StickyScrollPageHeader } from "@src/appV2/redesign/components/StickyScrollPageHeader";
import { useGetFacilityBreakConfiguration } from "@src/appV2/Shifts/Shift/useGetFacilityBreakConfiguration";
import { parseISO } from "date-fns";
import { useRef } from "react";

import { useScrollToTopOnStatusTap } from "../../useScrollToTopOnStatusTap";
import { WorkplaceNoteIdentifier } from "../constants";
import type { WorkplaceProfile } from "../types";
import { useWorkplaceNotesParsed } from "../useWorkplaceNotesParsed";
import { BreaksSection } from "./BreaksSection";
import { CancellationPoliciesSection } from "./CancellationPoliciesSection";
import { ExtraTimePaySection } from "./ExtraTimePaySection";
import { LateClockOutSection } from "./LateClockOutSection";
import { PolicyInformationSection } from "./PolicyInformationSection";
import { WorkBadgeRequiredSection } from "./WorkBadgeRequiredSection";

interface WorkplacePoliciesModalProps {
  modalState: UseModalState;
  workplace: WorkplaceProfile;
}

export function WorkplacePoliciesModal(props: WorkplacePoliciesModalProps) {
  const { modalState, workplace } = props;
  const {
    userId: workplaceId,
    lateCancellation,
    sentHomeChargeHours = 0,
    requiresLunchBreak,
    requiresLunchBreakSince,
    providesRestBreaks,
    providesRestBreaksSince,
  } = workplace;

  const { data: notes = {} } = useWorkplaceNotesParsed(workplaceId!);

  const { data: workplaceData } = useGetWorkplace(
    {
      workplaceId,
    },
    {
      enabled: isDefined(workplaceId),
    }
  );

  const {
    data: extraTimePaySettings,
    isError: extraTimePayIsError,
    isLoading: extraTimePayIsLoading,
  } = useGetExtraTimePaySettings(workplaceId);

  const cancelInstructionsNote = notes[WorkplaceNoteIdentifier.CANCEL_INSTRUCTIONS];
  const dressCodeNote = notes[WorkplaceNoteIdentifier.DRESS_CODE_INSTRUCTIONS];
  const importantInformationNote = notes[WorkplaceNoteIdentifier.PROFILE_DESCRIPTION];
  const mandatoryBreakNote = notes[WorkplaceNoteIdentifier.MANDATORY_BREAK_POLICY];
  const restBreakNote = notes[WorkplaceNoteIdentifier.REST_BREAK_POLICY];
  const lateClockOutNote = notes[WorkplaceNoteIdentifier.LATE_CLOCK_OUT_POLICY];
  const independentContractorBadgeSettings =
    workplaceData?.data?.attributes?.independentContractorBadge;
  const isIndependentContractorBadgeEnabled = independentContractorBadgeSettings?.enabled;
  const isIndependentContractorBadgeEnforcementEnabled =
    independentContractorBadgeSettings?.workplaceBadgeEnforcement?.enabled ?? false;

  const hasBreaks =
    (requiresLunchBreak === true && isDefined(mandatoryBreakNote)) ||
    (providesRestBreaks === true && isDefined(restBreakNote));

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useScrollToTopOnStatusTap(scrollContainerRef);

  const { mandatoryBreakDurationInMinutes } = useGetFacilityBreakConfiguration({
    workplaceId,
  });

  return (
    <FullScreenDialog
      skipTopSafeAreaPadding
      // fixes the Shifts tab being temporarily visible while the enter transition is happening
      transitionDuration={0}
      modalState={modalState}
      variant="primary"
      sx={{ overflow: "hidden" }}
    >
      <StickyScrollPageHeader
        title="Workplace Policies"
        scrollContainerRef={scrollContainerRef}
        isLoading={false}
      />

      <DialogContent ref={scrollContainerRef} sx={{ padding: 7, paddingTop: 0 }}>
        <Stack spacing={10}>
          {(isDefined(cancelInstructionsNote?.note) || isDefined(lateCancellation)) && (
            <InformationSection
              iconType="remove"
              title="Cancellation Payouts"
              id="cancellation-policies"
            >
              <CancellationPoliciesSection
                validAsOfDate={
                  cancelInstructionsNote?.updatedAt
                    ? parseISO(cancelInstructionsNote.updatedAt)
                    : new Date()
                }
                cancellationWorkplaceNote={cancelInstructionsNote?.note}
                lateCancellationPeriod={lateCancellation?.period ?? 0}
                lateCancellationFeeHours={lateCancellation?.feeHours ?? 0}
                sentHomeChargeHours={sentHomeChargeHours}
                textVariant="body1"
                textSx={{ lineHeight: 1.25 }}
                sx={{ overflow: "visible" }}
              />
            </InformationSection>
          )}

          {isIndependentContractorBadgeEnabled && (
            <InformationSection
              iconType="document-id-outlined"
              title="Badge Required"
              id="badge-required"
            >
              <WorkBadgeRequiredSection
                workBadgeEnforcementEnabled={isIndependentContractorBadgeEnforcementEnabled}
              />
            </InformationSection>
          )}

          {hasBreaks && (
            <InformationSection iconType="break" title="Breaks" id="breaks">
              <BreaksSection
                requiresLunchBreak={requiresLunchBreak}
                requiresLunchBreakSince={requiresLunchBreakSince}
                providesRestBreaks={providesRestBreaks}
                providesRestBreaksSince={providesRestBreaksSince}
                workplaceNotes={notes}
                mandatoryBreakDurationInMinutes={mandatoryBreakDurationInMinutes}
              />
            </InformationSection>
          )}

          {isDefined(lateClockOutNote?.note) && (
            <InformationSection iconType="clock" title="Late clock-outs" id="late-clock-outs">
              <LateClockOutSection lateClockOutNote={lateClockOutNote} />
            </InformationSection>
          )}

          {isDefined(extraTimePaySettings) && (
            <InformationSection iconType="extra-pay" title="Extra Time Pay" id="extra-time-pay">
              <ExtraTimePaySection
                settings={extraTimePaySettings}
                isError={extraTimePayIsError}
                isLoading={extraTimePayIsLoading}
              />
            </InformationSection>
          )}

          {isDefined(dressCodeNote?.note) && (
            <PolicyInformationSection
              id="dress-code"
              iconType="dress-code"
              title="Dress Code"
              note={dressCodeNote.note}
              updatedAt={dressCodeNote.updatedAt}
            />
          )}

          {isDefined(importantInformationNote?.note) && (
            <PolicyInformationSection
              id="other-policies"
              iconType="info"
              title="Other Policies"
              note={importantInformationNote.note}
              updatedAt={importantInformationNote.updatedAt}
            />
          )}
        </Stack>
      </DialogContent>
    </FullScreenDialog>
  );
}
